import { ReactElement, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import firstViewVideo from '@/assets/images/top/firstview.webm';
import { useLazyLoad } from '@/hooks/useLazyLoad';

/**
 * 記事下に設置する紹介用のコンポーネント
 */
function AppCallToAction(): ReactElement {
    const videoRef = useRef<HTMLVideoElement>(null);
    // 遅延読み込み
    const [ref] = useLazyLoad<HTMLDivElement>({ threshold: 0.1 });
    const location = useLocation();
    const pathname = location.pathname;
    const isEnglishArticle =
        pathname.startsWith('/en/articles/') || /\/articles\/en_/.test(pathname);

    const copy = isEnglishArticle
        ? {
              title: '📅 Visualize how you spend your time with Kotomil 🔍',
              description:
                  'Connect your calendars with Kotomil to turn your schedule into charts and insights in seconds.',
              bullet1: 'Visualize your events with charts and tables',
              bullet2: 'Check how much time you spent this month at a glance',
              bullet3: 'Focus on specific keywords and see where your time goes',
              ctaIntro: 'Check your own data from the link below ▼',
              linkLabel: 'Kotomil | kotomil.com',
          }
        : {
              title: '📅 カレンダーからあなたの時間の使い方を見える化 🔍',
              description:
                  'コトミルでカレンダー連携すると、期間やキーワードごとでグラフやデータなどでみることができます。',
              bullet1: 'スケジュールをグラフや表で見える化',
              bullet2: '今月何にどのくらいの割合を使ったかをひと目で確認',
              bullet3: 'キーワードを指定して、どのくらいの時間を使っているかを可視化',
              ctaIntro: '以下のリンクから簡単に結果を見ることができます ▼',
              linkLabel: 'コトミル | kotomil.com',
          };

    // メイン処理
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 3;
        }
    }, []);

    return (
        <>
            <div className="max-w-[800px] -mx-[10px] mt-12 pt-6 lg:px-2 lg:pt-10 bg-[#fffdf2] rounded-3xl text-base lg:text-lg">
                {/* タイトル */}
                <h2 className="text-2xl lg:text-3xl lg:pb-2 leading-normal lg:leading-relaxed mx-6 border-b-2 border-yellow-400 text-gray-700 tracking-widest">
                    {copy.title}
                </h2>
                <p className="mt-6 mx-6 tracking-widest leading-relaxed">{copy.description}</p>
                {/* メディア */}
                <div ref={ref}>
                    <video
                        ref={videoRef}
                        src={firstViewVideo}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="mt-6 mx-auto px-1 max-h-[400px] w-auto rounded-2xl border-4 border-white shadow-lg image-protection"
                    />
                </div>
                {/* 説明 */}
                <div className="pt-2 px-6">
                    <ul className="list-disc pl-4 mt-6 space-y-2 tracking-widest leading-relaxed">
                        <li>{copy.bullet1}</li>
                        <li>{copy.bullet2}</li>
                        <li>{copy.bullet3}</li>
                    </ul>
                    <p className="mt-6 tracking-widest leading-relaxed">{copy.ctaIntro}</p>
                    {/* リンク */}
                    <div className="flex flex-col items-center justify-center pt-14 pb-16">
                        <a
                            className="pb-1 w-auto text-xl tracking-widest text-blue-500 hover:text-blue-700 border-b border-blue-500 hover:border-blue-700"
                            href={`/?from=${encodeURIComponent(location.pathname + location.search)}`}
                        >
                            {copy.linkLabel}
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AppCallToAction;
